import React, { useMemo } from 'react';
import axios from 'axios';
import { useQuery, useMutation } from '@tanstack/react-query';
import Modal from '../../components/Modal';
import DataTable from '../../components/data_table';
import ErrorAlert from '../../components/error_alert';
import DateDisplay from '../../components/DateDisplay';

function fetchNotifications() {
    return axios({
        url: `${process.env.REACT_APP_API_URL}/subscription-notification`,
        method: 'GET',
        withCredentials: true,
    }).then((r) => r?.data);
}

function sendNotification(body) {
    return axios({
        url: `${process.env.REACT_APP_API_URL}/subscription-notification-send`,
        method: 'POST',
        data: body,
        withCredentials: true,
    }).then((r) => r?.data);
}

export default function SubscriptionNotificationDialog({ isOpen, onClose }) {
    const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
        queryKey: ['subscription-notification','list'],
        queryFn: fetchNotifications,
        enabled: !!isOpen,
    });

    const { mutate: triggerSend, isPending: sending } = useMutation({
        mutationKey: ['subscription-notification','send'],
        mutationFn: sendNotification,
        onSuccess: () => refetch(),
    });

    const rows = useMemo(() => {
        const list = Array.isArray(data?.data) ? data.data : [];
        return list.map((x, idx) => ({
            id: x.subscription_id ?? idx,
            clientId: x.client_id,
            clientName: x.client_name,
            subscriptionValid: x.subscription_valid,
            users: Array.isArray(x.users) ? x.users : [],
            notificationType: x.notificationType,
            raw: x,
        }));
    }, [data]);

    const columns = useMemo(() => ([
        { id: 'clientId', header: 'Client ID', accessorKey: 'clientId' },
        { id: 'clientName', header: 'Client Name', accessorKey: 'clientName' },
        { id: 'subscriptionValid', header: 'Valid Until', accessorKey: 'subscriptionValid', size: 160, Cell: ({cell}) => (<DateDisplay date={cell.getValue()} format="DD MMM YYYY" />) },
        { id: 'users', header: 'Users', accessorKey: 'users', size: 260, Cell: ({cell}) => {
                const arr = Array.isArray(cell.getValue()) ? cell.getValue() : [];
                if (!arr.length) return <span className="tw-text-gray-400">-</span>;
                return (
                    <div className="tw-text-sm tw-space-y-0.5">
                        {arr.map((u, i) => (
                            <div key={i}>{u.email || u.name || '-'}</div>
                        ))}
                    </div>
                );
            }
        },
        { id: 'notificationType', header: 'Notification Type', accessorKey: 'notificationType' },
        { id: 'actions', header: '', size: 120, Cell: ({row}) => (
                <div className="tw-flex tw-justify-end">
                    <button
                        className="tw-btn tw-btn-sm tw-btn-primary"
                        disabled={sending}
                        onClick={() => triggerSend({ clientId: row.clientId })}
                    >
                        {sending ? 'Sending…' : 'Send'}
                    </button>
                </div>
            )
        },
    ]), [triggerSend, sending]);

    const emptyStateConfig = {
        title: 'No reminder scheduled today',
        subtitle: '',
        actionButton: (
            <button className="tw-btn tw-btn-sm tw-btn-outline" onClick={() => refetch()} disabled={isLoading || isFetching}>
                Refresh
            </button>
        ),
        loadingMessage: 'Loading reminders…',
    };

    return (
        <Modal
            isOpen={!!isOpen}
            onClose={onClose}
            title="Subscription Reminders"
            subtitle="Today\'s scheduled notifications"
            size="tw-max-w-6xl"
        >
            <div className="tw-flex tw-flex-col tw-h-full tw-max-h-[70vh]">
                <ErrorAlert error={isError ? error : null} onRetry={refetch} isLoading={isLoading || isFetching} />
                <div className="tw-flex-1 tw-min-h-0 tw-overflow-auto">
                    <DataTable
                        data={rows}
                        columns={columns}
                        isLoading={isLoading || isFetching}
                        error={isError ? error : null}
                        onRetry={refetch}
                        emptyStateConfig={emptyStateConfig}
                        tableClassName="tw-w-full"
                    />
                </div>
                <div className="tw-flex tw-justify-end tw-pt-4 tw-border-t tw-mt-4">
                    <button className="tw-btn" onClick={onClose}>Close</button>
                </div>
            </div>
        </Modal>
    );
}

// ============================================================================
// PATCH: Update src/pages/subscriptions/ClientSubscription.jsx toolbar to add button
// ============================================================================
// In the imports, add:
//   import { RiNotification3Line } from 'react-icons/ri';
//   import SubscriptionNotificationDialog from './components/SubscriptionNotificationDialog';
//
// Inside component state:
//   const [notifOpen, setNotifOpen] = useState(false);
//
// Replace renderTopToolbarCustomActions with:
//   renderTopToolbarCustomActions: () => (
//     <span className="tw-flex-1 tw-flex tw-items-center tw-gap-1">
//       <IconButton onClick={openRegistrationDialog} title="New Subscription">
//         <IoMdAddCircleOutline className="tw-text-2xl" />
//       </IconButton>
//       <IconButton onClick={() => setNotifOpen(true)} title="Today\'s Reminders">
//         <RiNotification3Line className="tw-text-2xl" />
//       </IconButton>
//     </span>
//   ),
//
// And render the dialog near the bottom of the JSX:
//   <SubscriptionNotificationDialog isOpen={notifOpen} onClose={() => setNotifOpen(false)} />

